import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";

const STATUSES = ["pending", "approved", "rejected"];

export default function AdminPage() {
  const { setPending } = useOutletContext();
  const [status, setStatus] = useState("pending");
  const [users, setUsers] = useState(null);

  const load = (s) => api.get("/admin/users", { params: { status: s } }).then((r) => setUsers(r.data));
  useEffect(() => {
    load(status);
  }, [status]);

  const setUserStatus = async (u, s) => {
    try {
      await api.patch(`/admin/users/${u.id}/status`, { status: s });
      toast.success(`${u.name} ${s === "approved" ? "approved" : s === "rejected" ? "declined" : "moved to pending"}`);
      setUsers(users.filter((x) => x.id !== u.id));
      api.get("/admin/stats").then((r) => setPending(r.data.pending));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-page">
      <div className="label-accent">Host tools</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-1">Member requests</h1>
      <div className="flex gap-2 mt-6">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`admin-filter-${s}`}
            className={`px-4 py-1.5 rounded-full text-sm capitalize border ${status === s ? "bg-[var(--hb-ink)] text-white border-[var(--hb-ink)]" : "bg-white border-[var(--hb-line)]"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="hb-card mt-6 divide-y divide-[var(--hb-line)]">
        {users?.filter((u) => u.role !== "admin").length === 0 && (
          <p className="p-8 text-center text-[var(--hb-muted)]" data-testid="admin-empty">No {status} members.</p>
        )}
        {users?.filter((u) => u.role !== "admin").map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`admin-user-${u.id}`}>
            <UserAvatar user={u} size={44} />
            <div className="flex-1 min-w-[180px]">
              <div className="font-semibold">{u.name}</div>
              <div className="text-xs text-[var(--hb-muted)]">{u.email}{u.business ? ` · ${u.business}` : ""} · requested {timeAgo(u.created_at)}</div>
            </div>
            <div className="flex gap-2">
              {status !== "approved" && (
                <button className="btn-primary !py-2" onClick={() => setUserStatus(u, "approved")} data-testid={`admin-approve-${u.id}`}><Check size={14} /> Approve</button>
              )}
              {status !== "rejected" && (
                <button className="btn-ghost" onClick={() => setUserStatus(u, "rejected")} data-testid={`admin-reject-${u.id}`}><X size={13} /> {status === "approved" ? "Remove" : "Decline"}</button>
              )}
              {status === "rejected" && (
                <button className="btn-ghost" onClick={() => setUserStatus(u, "pending")} data-testid={`admin-reset-${u.id}`}><RotateCcw size={13} /> Pending</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
